import moment from 'moment'
import { put, select, takeLatest, debounce, all, call } from 'redux-saga/effects'

import { strings } from 'context/LanguageContext/strings'
import { 
    trackRafCodeRedemption,
    trackCateringCartAbandonmentRateItemAdded,
    trackCateringSalesOrderCompleted,
    trackCateringSalesItemSold,
    trackCateringBasketSizeCalculated,
    trackCateringConversionRateCheckoutCompleted,
    trackCateringCancelledOrdersCancellationInitiated,
    trackCateringCancelledOrdersOrderCancelled
} from 'middleware/helper'
import { actions as appSettingActions } from 'modules/appSettings/actions'
import { getGuestUniqueID } from 'modules/authentication/sagas'
import {
    actions,
    patchCartStart,
    patchCartSuccess,
    patchCartFailed,
    setBasketItems,
    getBasketSuccess,
    getBasketFailedDone,
    checkoutSuccess,
    checkoutFailed,
    getSuggestiveSellingSuccess,
    getSuggestiveSellingFailed,
    placeOrderFailed,
    placeOrderSuccess,
    applyPromoCodeFailed,
    removePromoCodeFailed,
    setDeferredPatchRequests,
    patchBasketItem,
    updateShippingDone,
    setRefreshBasketRequired,
    reorderDone,
    placeOrderViewed,
    promoCodeResult,
    resetDeliveryNotes,
    applyReferralCodeSuccess,
    applyReferralCodeFailed,
    trackSuggestiveItems,
    preOrderCancelFailed,
    preOrderCancelSuccess,
} from 'modules/basket/actions'
import {
    parseBasketResponse,
    parseBasketItems,
    suggestiveSellingRequest,
    parseSuggestiveSellingResponse,
    getPaymentDetails,
    parseCheckoutResponse,
} from 'modules/basket/index'
import { cartUrl, suggestiveSellingUrl, checkoutUrl, placeOrderUrl, preOrderCancelUrl } from 'modules/basket/urls'
import { actions as profileUpdateActions } from 'modules/user/actions'
import { api, getIPAddress } from 'network/api'
import { util } from 'utils'
import { getAppVersion } from 'utils/appConfiguration'
import { isDeliveryMode, isEmpty, itemOperations, orderTypes } from 'utils/appUtils'
import { language } from 'context/LanguageContext/strings'
import dayjs from 'dayjs'
import { triggerAllAPIsForLocationUpdate } from 'modules/location/sagas'
import { hasCateringData } from 'utils/cateringStorage'
import { getOrderStatus } from 'modules/orders/actions'

const getLocalTime = () => moment().toISOString(true)

function* addRemoveItem(action) {
    try {
        const { item, operation, shouldUpdateCart, mixpanelFlagForAi, mixpanelFlagForSearch } =
            action.payload
        if (!item) return
        const { basket } = yield select()
        const savedBasket = basket.toJS()
        const basketItems = savedBasket.basketItems
        const { exists } = util.productExistsInCart(item, basketItems)
        if (exists) {
            const itemIndex = basketItems.findIndex(
                product => product.product_id === item.product_id
            )
            if (itemIndex >= 0) {
                if (!operation || operation === itemOperations.ADD) {
                    basketItems[itemIndex].count++
                } else {
                    basketItems[itemIndex].count--
                }
            }
        } else {
            if (!operation || operation === itemOperations.ADD) {
                item.count = 1 //Initially the count should be 0
                basketItems.push(item)
            }
        }
        yield put(setBasketItems(basketItems))

        yield put(
            patchBasketItem(
                basketItems,
                false,
                true,
                { operation, item },
                mixpanelFlagForAi,
                mixpanelFlagForSearch
            )
        )
    } catch (error) {
        console.log(' Item added error ', error)
    }
}

function* reorderItems(items) {
    try {
        const { basket } = yield select()
        const savedBasket = basket.toJS()
        const basketItems = savedBasket.basketItems
        for (let i = 0; i < items.length; i++) {
            let pastOrderItem = items[i]
            const { exists } = util.productExistsInCart(pastOrderItem, basketItems)
            if (exists) {
                const itemIndex = basketItems.findIndex(
                    product => product.product_id === pastOrderItem.product_id
                )
                let limitPerOrder = basketItems[itemIndex].limit_per_order
                if (itemIndex >= 0) {
                    basketItems[itemIndex].count += pastOrderItem.quantity
                    basketItems[itemIndex].count =
                        basketItems[itemIndex].count > limitPerOrder
                            ? limitPerOrder
                            : basketItems[itemIndex].count
                }
            } else {
                pastOrderItem.count = pastOrderItem.quantity
                basketItems.push(pastOrderItem)
            }
            //doesnt exist ouofstock
        }
        yield put(setBasketItems(basketItems))
        yield put(patchBasketItem(basketItems, true))
    } catch (error) {
        console.log(' Item added error ', error)
    }
}

function* callPatchBasketItem(action) {
    try {
        const { location, basket } = yield select()
        const basketItems = action.payload.items || []
        const isReorder = action.payload.isReorder
        const itemsUnavailable = action.payload.itemsUnavailable
        const hasPlatterItems = basket.get('hasPlatterItems')
        if (basketItems.length <= 0) return

        let deferredRequests = basket ? basket.toJS().deferredPatchRequests : []
        const updateRequest = {
            items: basketItems.map(product => ({
                product_id: product.product_id,
                quantity: product.count,
                name: product.name,
                product_oos_option: product.product_oos_option || 'REPLACE',
                platter_item_flag: product?.platter_item_flag,
            })),
            customer_local_time: getLocalTime(),
            out_of_stock_acknowledgement: itemsUnavailable?.length ? true : false,
        }

        // Set session flag when platter items are added
        const hasPlatterItemsInRequest = basketItems.some(item => item?.platter_item_flag === '1')
        if (hasPlatterItemsInRequest) {
            sessionStorage.setItem('platter_items_added_this_session', 'true')
        }

        // Get store from appropriate location based on mode
        let selectedStore = location.get('selectedStore')
        if (hasPlatterItems) {
            const { catering } = yield select()
            selectedStore = catering?.store
        }
        
        if (selectedStore) {
            const storeId = selectedStore.get ? selectedStore.get('store_id') : selectedStore.store_id
            updateRequest.store_id = storeId
        }

        if (isReorder) {
            yield put(reorderDone(true))
        }

        yield put(patchCartStart())
        const requestID = Date.now()
        deferredRequests.push(requestID)
        yield put(setDeferredPatchRequests(deferredRequests))
        // console.log(" before patch call ", deferredRequests )
        const response = yield call(api, cartUrl(), 'POST', updateRequest, 1)
        //Remove the request id from deferred List

        yield put(setDeferredPatchRequests([]))

        const parsedResponse = parseBasketResponse(response)
        yield put(
            patchCartSuccess(
                parsedResponse,
                action.payload.trackingData,
                action.payload.mixpanelFlagForAi,
                action.payload.mixpanelFlagForSearch
            )
        )
        
        // Track catering item added and basket size
        if (hasPlatterItemsInRequest && action.payload.trackingData?.operation === 'add') {
            const item = action.payload.trackingData.item
            if (item) {
                trackCateringCartAbandonmentRateItemAdded(
                    item.product_id,
                    item.name,
                    item.category || '',
                    item.price || 0,
                    item.count || 1
                )
            }
            
            // Track basket size
            const totalItems = parsedResponse.basketResponse?.items?.reduce((sum, i) => sum + i.quantity, 0) || 0
            const basketValue = parsedResponse.basketResponse?.totalBeforeTax?.value || 0
            trackCateringBasketSizeCalculated(totalItems, basketValue)
        }

        // console.log('parsedResponse', parsedResponse)
        yield* updateCartIfNeeded(response, parsedResponse, true)
    } catch (error) {
        console.log('item card patch failed ', error)
        yield put(setDeferredPatchRequests([]))
        yield put(patchCartFailed(error))
    }
}

export function* updateShipping() {
    try {
        const { location, user, basket, appSettings } = yield select()
        const selectedStore = location.get('selectedStore')
        const selectedAddress = location.get('selectedAddress')
        const { catering } = yield select()
        const hasPlatterItems = basket.get('hasPlatterItems')
        const isCateringMode = hasPlatterItems || appSettings.get('isCateringMode')

        // In catering mode, get address and store from catering state
        let addressToUse = selectedAddress
        let storeToUse = selectedStore
        if (isCateringMode) {
            addressToUse = catering?.address
            storeToUse = catering?.store
        }
        const profile = user.get('userProfile') ? user.get('userProfile').toJS() : null
        if (!storeToUse || !addressToUse) {
            return
        }

        const store = storeToUse.toJS ? storeToUse.toJS() : storeToUse
        const shippingInfo = yield getShippingInfo()
        const ip = yield getIPAddress()

        const payload = {
            store_id: store.store_id || store.id,
            customer_local_time: getLocalTime(),
            customer_ip_address: ip || '',
            shipping: shippingInfo,
        }

        if (profile) {
            payload.user_profile = {
                first_name: profile.first_name,
                last_name: profile.last_name,
                phone_number: profile.phone_number,
                email: profile.email,
            }
        }
        if(isCateringMode && catering?.schedule){
            payload.schedulePickUpDate = catering.schedule.date
            payload.schedulePickUpTime = catering.schedule.time
        }
        yield put(patchCartStart())
        const response = yield call(api, cartUrl(), 'POST', payload, 1)
        const parsedResponse = parseBasketResponse(response)
        yield put(patchCartSuccess(parsedResponse))
        yield* updateCartIfNeeded(response, parsedResponse)
        yield put(updateShippingDone())
    } catch (error) {
        console.log('UPDATE SHIPPING FAILED ', error)
        yield put(patchCartFailed(error))
    }
}

export function* updateShipping_DeliveryNotes() {
    try {
        const { basket } = yield select()
        const deliveryNotes = basket.get('deliveryNotes')
        const payload = {
            shipping: {
                delivery_notes: deliveryNotes,
            },
        }
        yield call(api, cartUrl(), 'POST', payload, 1)
    } catch (error) {
        console.log('erroe', error)
        yield put(patchCartFailed(error))
    }
}

export function* updateShipping_PhoneNumber(action) {
    try {
        const { location, user } = yield select()
        const selectedStore = location.get('selectedStore')
        const selectedAddress = location.get('selectedAddress')
        const profile = user.get('userProfile') ? user.get('userProfile').toJS() : null
        if (!selectedStore || !selectedAddress) return

        const store = selectedStore.toJS()
        const shippingInfo = yield getShippingInfo()

        const payload = {
            store_id: store.store_id || store.id,
            customer_local_time: getLocalTime(),
            // customer_ip_address: ip, //TO DO
            shipping: shippingInfo,
        }

        if (profile) {
            payload.user_profile = {
                first_name: profile.first_name,
                last_name: profile.last_name,
                phone_number: action.payload,
                email: profile.email,
            }
        }
        yield call(api, cartUrl(), 'POST', payload, 1)
    } catch (error) {
        console.log('UPDATE SHIPPING FAILED ', error)
        yield put(patchCartFailed(error))
    }
}

function* updateCartIfNeeded(response, parsedResponse, updateCart = false) {
    try {
        if (response && response.items) {
            const basketItems = parseBasketItems(response.items, parsedResponse.basketUpdates)
            //If there are any invalids, update cart
            if (updateCart) {
                yield put(setBasketItems(basketItems))
                if (
                    parsedResponse &&
                    parsedResponse.basketUpdates &&
                    Object.keys(parsedResponse.basketUpdates).length > 0
                ) {
                    yield* updateCartWithLocalItems(response.items, parsedResponse.basketUpdates)
                }
            }
        } else {
            if (updateCart) yield put(setBasketItems([]))
        }
    } catch (error) {
        console.log(' Error in updateCartIfNeeded is ', error)
    }
}

function* getShippingInfo() {
    const { location, basket, appSettings } = yield select()
    const selectedAddress = location.get('selectedAddress')
    const hasPlatterItems = basket.get('hasPlatterItems')
    const isCateringMode = hasPlatterItems || appSettings.get('isCateringMode')
    
    // In catering mode, get address from catering state
    let addressToUse = selectedAddress
    if (isCateringMode) {
        const { catering } = yield select()
        addressToUse = catering?.address
    }
    
    if (!addressToUse) return null

    const address = addressToUse.toJS ? addressToUse.toJS() : addressToUse
    if (isDeliveryMode()) {
        return {
            dropoff: {
                street: address.street,
                apt: address.apt,
                city: address.city,
                state: address.state,
                zip: address.zip,
                latitude: address.lattitude,
                longitude: address.longitude,
                country: address.country,
            },
        }
    }

    return {
        pickup: {
            street: address.street,
            apt: address.apt,
            city: address.city,
            state: address.state,
            zip: address.zip,
            latitude: address.lattitude,
            longitude: address.longitude,
            country: address.country,
        },
    }
}

//This method should be called any time the whole cart needs to be patched
function* updateCartWithLocalItems(items, updates) {
    try {
        //We need to patch the new quantities and remove the old items from the cart
        let requestItems = items.map(product => ({
            product_id: product.product_id,
            quantity: product.quantity,
            name: product.name,
            product_oos_option: product.product_oos_option || 'REPLACE',
            platter_item_flag: product?.platter_item_flag,
        }))

        const { itemsUnavailable } = updates
        const { location, basket, appSettings } = yield select()
        const hasPlatterItems = basket.get('hasPlatterItems')
        const isCateringMode = hasPlatterItems || appSettings.get('isCateringMode')
        
        // Use catering store when in catering mode
        let store
        if (isCateringMode) {
            const { catering } = yield select()
            const cateringStore = catering?.store
            store = cateringStore?.toJS ? cateringStore.toJS() : cateringStore
        } else {
            const selectedStore = location.get('selectedStore')
            store = selectedStore?.toJS ? selectedStore.toJS() : selectedStore
        }
        
        if (!store) return
        // if (itemsUnavailable?.length > 0) {
        //     for (let loop = 0; loop < itemsUnavailable.length; loop++) {
        //         const item = itemsUnavailable[loop]
        //         requestItems.push({
        //             product_id: item.product.product_id,
        //             quantity: 0,
        //             name: item.product.name,
        //             product_oos_option: item.product.product_oos_option || 'REPLACE',
        //         })
        //     }
        // }
        const request = {
            items: requestItems,
            customer_local_time: getLocalTime(),
            store_id: store.store_id || store.id,
        }
        yield put(patchCartStart())
        const response = yield call(api, cartUrl(), 'POST', request, 1)
        const parsedResponse = parseBasketResponse(response)
        yield put(patchCartSuccess(parsedResponse))
    } catch (error) {
        console.log('updateCartWithLocalItems ', error)
        yield put(patchCartFailed(error))
    }
}

export function* getCart() {
    try {
        const { basket, appSettings } = yield select()
        const deferredPatchRequests = basket ? basket.toJS().deferredPatchRequests : []
        let timeStamp = new Date().getTime()
        // Check if the patch Cart is in progress, if yes no need to call the get cart
        if (deferredPatchRequests.length > 0) {
            yield put(getBasketFailedDone())
            yield put(setRefreshBasketRequired(true))
            return
        }

        const { isSignedIn } = util.getJWTDetails()
        const response = yield call(
            api,
            `${cartUrl()}?timeStamp=${timeStamp}`,
            'GET',
            null,
            1,
            null,
            false,
            language
        )

        const parsedResponse = parseBasketResponse(response)
        
        // Auto-clear platter items only when tab is closed and reopened
        const basketItems = parsedResponse?.basketResponse?.items || []
        const hasPlatterItemsInCart = basketItems.some(item => item?.platter_item_flag === '1')
        
        if (hasPlatterItemsInCart) {
            const itemsAddedThisSession = sessionStorage.getItem('platter_items_added_this_session')
            const hasValidCateringData = hasCateringData()
            
            // Only clear if items were NOT added in this session AND no valid catering data
            // This means tab was closed/reopened (session storage cleared)
            if (!itemsAddedThisSession && !hasValidCateringData) {
                const { location } = yield select()
                const storeLocation = location.toJS()
                const storeDetails = storeLocation.selectedStore
                
                if (storeDetails) {
                    const clearRequest = {
                        items: basketItems.map(item => ({
                            product_id: item.product_id,
                            quantity: 0,
                            name: item.name,
                            product_oos_option: item.product_oos_option || 'REMOVE',
                            platter_item_flag: item?.platter_item_flag,
                        })),
                        customer_local_time: getLocalTime(),
                        store_id: storeDetails?.store_id?.toString(),
                    }
                    yield put(patchCartStart())
                    const clearResponse = yield call(api, cartUrl(), 'POST', clearRequest, 1)
                    yield put(setBasketItems([]))
                    const clearedParsedResponse = parseBasketResponse(clearResponse)
                    yield put(patchCartSuccess(clearedParsedResponse))
                    return
                }
            }
        }
        
        yield put(getBasketSuccess(parsedResponse))
        yield* updateCartIfNeeded(response, parsedResponse, true)

        if (
            isSignedIn &&
            (!response.store_id ||
                !response.store_details ||
                !response.user_profile ||
                !response.user_profile.first_name ||
                !response.shipping ||
                !response.shipping.pickup)
        ) {
            yield* updateShipping()
        }
    } catch (error) {
        yield put(getBasketFailedDone(error))
    }
}

function* checkout() {
    try {
        const { appSettings } = yield select()
        const phone = appSettings.get('phoneNumber')
        const checkoutRequest = {}

        if (isDeliveryMode() && phone) {
            checkoutRequest.phone_number = phone
        }

        const checkoutResponse = yield call(
            api,
            checkoutUrl(),
            'POST',
            checkoutRequest,
            1,
            null,
            false,
            language
        )

        if (checkoutResponse && checkoutResponse.cart_hash) {
            const parsedResponse = parseCheckoutResponse(checkoutResponse)
            yield put(checkoutSuccess(parsedResponse))
            yield put(placeOrderViewed())
        } else {
            yield put(checkoutFailed(checkoutResponse))
        }
    } catch (error) {
        yield put(checkoutFailed(error))
    }
}

function* placeOrder(action) {
    try {
        const tip = action.payload
        const { wallet, appSettings } = yield select()
        const { user, basket, catering } = yield select()
        const { selectedCard, defaultCard } = wallet.toJS()
        const { isApp, appDetails } = appSettings.toJS()
        const card = selectedCard ? selectedCard : defaultCard ? defaultCard : null
        const userProfile = user.toJS().userProfile
        const source = !isEmpty(appDetails) ? appDetails.source || '711app' : 'W'

        if (!userProfile) return

        const { digital_id } = userProfile
        const checkoutResponse = basket.toJS().checkoutResponse

        const { cart_hash, store_id, total } = checkoutResponse

        const tip_details = isDeliveryMode() ? tip : null
        const orderTotal = isDeliveryMode() ? tip.order_total : total

        const payment_details = getPaymentDetails(card, store_id, digital_id, orderTotal)

        const uniqueId = yield* getGuestUniqueID()
        const ip = yield getIPAddress()

        const hasPlatterItems = basket.get('hasPlatterItems')
        const { schedule } = catering
        const payload = {
            hash: cart_hash,
            customer_local_time: getLocalTime(),
            customer_ip_address: ip || '',
            device_unique_identifier: uniqueId || '',
            app_version: getAppVersion(),
            os_version: 'N/A',
            device: source,
            tip_details,
            payment_details,
            // includeUtensils: preOrderUtensilOptions === 'Yes'
        }
        if (hasPlatterItems && schedule) {
            payload.schedulePickUpDate = schedule.date
            payload.schedulePickUpTime = schedule.time
        }
        const placeOrderResponse = yield call(api, placeOrderUrl(), 'POST', payload)
        yield* processPlaceOrderResponse(placeOrderResponse)
    } catch (error) {
        console.log(' Place Order failed ', error)
        yield put(placeOrderFailed(error))
        if (error.status === 409) {
            yield put(placeOrderFailed(error))
        } else if (
            error.status === 504 &&
            error.data &&
            error.data.details &&
            error.data.details.message === 'Endpoint request timed out'
        ) {
            yield put(placeOrderFailed(strings.placeOrderTimeOutError))
        } else {
            yield put(placeOrderFailed(strings.placeOrderGenericError))
        }
    }
}

function* processPlaceOrderResponse(placeOrderResponse) {
    try {
        const { user, basket, catering } = yield select()
        const userProfile = user.toJS().userProfile
        if (!userProfile) return

        const orderId =
            placeOrderResponse &&
            placeOrderResponse.order_id &&
            placeOrderResponse.order_id.length > 0
        const orderStatus =
            placeOrderResponse &&
            placeOrderResponse.order_status &&
            placeOrderResponse.order_status.length > 0

        const incorrectZip =
            placeOrderResponse &&
            placeOrderResponse.transaction_message &&
            placeOrderResponse.transaction_description
        
        const isPreOrder = placeOrderResponse?.isPreOrder
        
        // Check if this is a catering order
        const basketItems = basket.get('basketItems')?.toJS() || []
        const hasPlatterItems = basketItems.some(item => item?.platter_item_flag === '1')
        const isCateringOrder = hasPlatterItems || !!(catering?.schedule?.isValid)
        
        if (orderId) {
            if (
                orderStatus &&
                (
                    (isPreOrder && placeOrderResponse.order_status.toUpperCase() === 'PREORDER') ||
                    (!isPreOrder && placeOrderResponse.order_status.toUpperCase() === 'SUBMITTED')
                )
            ) {
                // Track catering order completion events
                if (isCateringOrder) {
                    const itemsCount = placeOrderResponse.original_items?.length || 0
                    const totalValue = placeOrderResponse.total ? placeOrderResponse.total / 100 : 0
                    const paymentMethod = placeOrderResponse.payment?.brand || 'unknown'
                    const storeId = catering?.store?.store_id || null
                    
                    // Track conversion rate checkout completed
                    trackCateringConversionRateCheckoutCompleted(
                        placeOrderResponse.order_id,
                        itemsCount,
                        totalValue,
                        paymentMethod
                    )
                    
                    // Track sales order completed
                    const itemDetails = placeOrderResponse.original_items?.map(item => ({
                        item_id: item.product_id || item.slin,
                        name: item.name,
                        category: item.category || '',
                        price: item.price ? item.price / 100 : 0,
                        qty: item.quantity || 1
                    })) || []
                    
                    trackCateringSalesOrderCompleted(
                        placeOrderResponse.order_id,
                        totalValue,
                        itemsCount,
                        itemDetails,
                        storeId,
                        'pickup', // catering is always pickup
                        'USD'
                    )
                    
                    // Track individual item sales
                    placeOrderResponse.original_items?.forEach(item => {
                        trackCateringSalesItemSold(
                            item.product_id || item.slin,
                            item.name,
                            item.category || '',
                            item.price ? item.price / 100 : 0,
                            item.quantity || 1,
                            placeOrderResponse.order_id,
                            userProfile.loyalty_id
                        )
                    })
                }
                
                yield put(placeOrderSuccess(placeOrderResponse))
                yield put(resetDeliveryNotes())
                yield* getCart()
            } else if (
                incorrectZip &&
                placeOrderResponse.transaction_message.toLowerCase() === 'incorrect_zip'
            ) {
                yield put(placeOrderFailed(placeOrderResponse.transaction_description))
            } else {
                yield put(placeOrderFailed(strings.placeOrderPaymentError))
            }
        } else {
            yield put(placeOrderFailed(strings.placeOrderGenericError))
        }
    } catch (error) {
        console.log(' There  was some error in place order parsing ', error)
    }
}

export function* getSuggestions() {
    try {
        const { location, basket } = yield select()
        const selectedStore = location.get('selectedStore')
        const savedBasket = basket?.toJS()
        const basketItems = savedBasket.basketItems
        const store = selectedStore?.toJS()
        if (!store || (basketItems && basketItems.length <= 0)) return
        const request = suggestiveSellingRequest(store.store_id, basketItems)
        const response = yield call(api, suggestiveSellingUrl, 'POST', request, 1)

        const parsedResponse = parseSuggestiveSellingResponse(response)
        yield put(trackSuggestiveItems(response))
        yield put(getSuggestiveSellingSuccess(parsedResponse))
    } catch (error) {
        yield put(getSuggestiveSellingFailed(error))
    }
}

function* clearBasketItems() {
    try {
        const { basket } = yield select()
        const savedBasket = basket.toJS()
        const basketItems = savedBasket.basketItems
        const { location } = yield select()
        const storeLocation = location.toJS()
        const storeDetails = storeLocation.selectedStore
        if (basketItems && basketItems.length <= 0) {
            return
        }

        const hasPlatterItems = basketItems.some(item => item?.platter_item_flag === '1')
        let storeId = storeDetails?.store_id?.toString()
        if (hasPlatterItems) {
            const { catering } = yield select()
            storeId = catering?.store?.store_id?.toString()
        }

        const request = {
            items: basketItems.map(item => ({
                product_id: item.product_id,
                quantity: 0,
                name: item.name,
                product_oos_option: item.product_oos_option || 'REMOVE',
                platter_item_flag: item?.platter_item_flag,
            })),
            customer_local_time: getLocalTime(),
            store_id: storeId,
        }
        yield put(patchCartStart())
        const response = yield call(api, cartUrl(), 'POST', request, 1)
        yield put(setBasketItems([]))
        const parsedResponse = parseBasketResponse(response)
        yield put(patchCartSuccess(parsedResponse))
    } catch (error) {
        yield put(patchCartFailed(error))
    }
}

export function* updateCartItem({ key, value, index }) {
    const { basket } = yield select()
    let basketItems = basket.get('basketItems')

    basketItems = basketItems.update(index, item => {
        return item.set(key, value)
    })
    yield* callPatchBasketItem({ payload: { items: basketItems.toJS() } })
}

function* applyBasketPromoCode(promoCode) {
    try {
        const payload = {
            coupons: [promoCode.payload],
        }

        const response = yield call(api, cartUrl(), 'POST', payload, 1)
        if (response && response.items && response.items.length > 0) {
            const basketItems = parseBasketItems(response.items)
            if (basketItems && basketItems.length > 0) {
                yield put(setBasketItems(basketItems))
            }
        }
        const parsedResponse = parseBasketResponse(response)
        yield put(patchCartSuccess(parsedResponse))
        const promoResonse =
            parsedResponse.basketResponse && parsedResponse.basketResponse.promoCodeResponse
        yield put(promoCodeResult(promoResonse))
    } catch (error) {
        console.log('PROMO FAILED', error)
        yield put(applyPromoCodeFailed(error))
    }
}

function* removeBasketPromoCode() {
    try {
        const coupons = []
        const payload = {
            coupons,
        }

        const response = yield call(api, cartUrl(), 'POST', payload, 1)
        if (response && response.items && response.items.length > 0) {
            const basketItems = parseBasketItems(response.items)
            if (basketItems && basketItems.length > 0) {
                yield put(setBasketItems(basketItems))
            }
        }
        const parsedResponse = parseBasketResponse(response)
        yield put(patchCartSuccess(parsedResponse))
    } catch (error) {
        yield put(removePromoCodeFailed(error))
    }
}

function* reorder({ payload: order }) {
    // console.log('saga-reorder', order)
    if (order === null || order === undefined || (order && order.length === 0)) {
        return
    }
    if (
        order.items === null ||
        order.items === undefined ||
        (order.items && order.items.length === 0)
    ) {
        return
    }
    yield* reorderItems(order.items)
}

export function* updateContactLessOption() {
    try {
        const state = yield select()
        const toggleValue = state.user.get('noContactDropOff')
        const payload = {
            contact_less_delivery: toggleValue,
        }
        yield call(api, cartUrl(), 'POST', payload, 1)
    } catch (error) {
        yield put(patchCartFailed(error))
    }
}

export function* updateProfilePatch() {
    try {
        const state = yield select()
        const userProfile = state.user.get('userProfile')

        if (!userProfile) return

        const profile = userProfile.toJS()
        const payloadUserProfile = {
            first_name: profile.first_name,
            last_name: profile.last_name,
            phone_number: profile.phone_number,
            email: profile.email,
        }
        if (profile.loyalty_id) {
            payloadUserProfile.loyalty_id = profile.loyalty_id
        }
        const payload = {
            user_profile: payloadUserProfile,
        }
        const response = yield call(api, cartUrl(), 'POST', payload, 1)
        const parsedResponse = parseBasketResponse(response)
        yield put(patchCartSuccess(parsedResponse))
    } catch (error) {
        yield put(patchCartFailed(error))
    }
}

function* applyReferralPromoCode(action) {
    try {
        const referral = { referral_code: action.payload, source: 'basket' }
        const response = yield call(api, cartUrl(), 'POST', { referral }, 1)

        if (response?.referral?.redemption_response) {
            const parsedResponse = parseBasketResponse(response)
            yield put(patchCartSuccess(parsedResponse))
            const referralResponse = parsedResponse?.basketResponse?.referralCodeResponse
            yield put(applyReferralCodeSuccess(referralResponse))
            if (referralResponse?.redemption_response?.redeemed === true) {
                trackRafCodeRedemption(strings.checkout)
            }
        } else {
            yield put(applyReferralCodeFailed())
        }
        // this is only used when redeemed is true for Mix Panel.
        // if (response?.referral?.redemption_response?.redeemed) {
        //     yield put(trackReferralCodeRedemeed('Basket'))
        // }
    } catch (error) {
        yield put(applyReferralCodeFailed(error))
    }
}

function* preOrderCancel(action) {
    try{
        const { order, selectedOption } = action?.payload
        const { catering } = yield select()
        
        // Check if this is a catering order
        const hasPlatterItems = order?.original_items?.some(item => item?.platter_item_flag === '1')
        const hasCateringSchedule = !!(catering?.schedule?.isValid)
        const isCateringOrder = hasPlatterItems || hasCateringSchedule
        
        // Track cancellation initiated for catering orders
        if (isCateringOrder) {
            const orderPlacedTime = order?.order_placed_time || order?.created_at
            const timeSinceOrder = orderPlacedTime 
                ? Math.floor((Date.now() - new Date(orderPlacedTime).getTime()) / 1000 / 60) // minutes
                : null
            
            trackCateringCancelledOrdersCancellationInitiated(
                order.order_id,
                order.order_status,
                timeSinceOrder
            )
        }
        
        const url = preOrderCancelUrl.replace('{order_id}', order.order_id)
        const payload = {
            deviceId: null,
            status: "canceled",
            reason: selectedOption,
            cancelTriggeredBy: "Customer"
        }
        const preOrderCancelResponse = yield call(api, url, 'PUT', payload)
        
        // Track order cancelled for catering orders
        if (isCateringOrder) {
            const itemsCount = order?.original_items?.length || 0
            const orderValue = order?.total ? order.total / 100 : 0
            
            trackCateringCancelledOrdersOrderCancelled(
                order.order_id,
                orderValue,
                itemsCount,
                selectedOption || 'unknown'
            )
        }
        
        yield put(preOrderCancelSuccess(preOrderCancelResponse))
        yield put(getOrderStatus(order.order_id))
    }
    catch(error){
        yield put(preOrderCancelFailed(error))
    }
}

function* setPreOrderDateTimeDetailsSaga(action){
    const { catering } = yield select()
    yield* triggerAllAPIsForLocationUpdate(catering?.address)
}

//=========================================
function* itemOperationWatcher() {
    yield takeLatest([actions.ADD_REMOVE_ITEM], addRemoveItem)
}

function* itemUpdateWatcher() {
    yield debounce(2000, [actions.PATCH_BASKET_ITEM], callPatchBasketItem)
}

function* updateShippingWatcher() {
    yield takeLatest(actions.UPDATE_SHIPPING, updateShipping)
}

function* phoneNumberUpdateWatcher() {
    yield debounce(1000, [appSettingActions.PATCH_PHONE_NUMBER], updateShipping_PhoneNumber)
}

function* getCartWatcher() {
    yield takeLatest(actions.GET_BASKET, getCart)
}

function* suggestiveSellingWatcher() {
    yield takeLatest(actions.GET_SUGGESTIVE_SELLING, getSuggestions)
}

function* checkoutWatcher() {
    yield takeLatest(actions.CHECKOUT, checkout)
}

function* placeOrderWatcher() {
    yield takeLatest(actions.PLACE_ORDER, placeOrder)
}

function* clearBasketWatcher() {
    yield takeLatest(actions.CLEAR_BASKET, clearBasketItems)
}

function* updateCartItemPropertyWatcher() {
    yield takeLatest(actions.UPDATE_CART_ITEM, updateCartItem)
}

function* applyBasketPromoWatcher() {
    yield takeLatest(actions.APPLY_PROMO_CODE, applyBasketPromoCode)
}

function* removeBasketPromoWatcher() {
    yield takeLatest(actions.REMOVE_PROMO_CODE, removeBasketPromoCode)
}

function* reorderWatcher() {
    yield takeLatest(actions.REORDER_ITEMS, reorder)
}

function* deliveryNotesUpdateWatcher() {
    yield debounce(1500, [actions.SET_DELIVERY_NOTES], updateShipping_DeliveryNotes)
}

function* updateProfileWatcher() {
    yield takeLatest(profileUpdateActions.UPDATE_USER_PROFILE_SUCCESS, updateProfilePatch)
}

function* applyReferralCodeWatcher() {
    yield takeLatest(actions.APPLY_REFERRAL_CODE, applyReferralPromoCode)
}

function* preOrderCancelWatcher() {
    yield takeLatest(actions.PREORDER_CANCEL, preOrderCancel)
}

function* setPreOrderDateTimeWatcher() {
    yield takeLatest(actions.SET_PRE_ORDER_DATE_TIME_DETAILS, setPreOrderDateTimeDetailsSaga)
}

export default function* basketSaga() {
    yield all([
        itemOperationWatcher(),
        updateShippingWatcher(),
        itemUpdateWatcher(),
        getCartWatcher(),
        placeOrderWatcher(),
        checkoutWatcher(),
        suggestiveSellingWatcher(),
        updateCartItemPropertyWatcher(),
        clearBasketWatcher(),
        applyBasketPromoWatcher(),
        removeBasketPromoWatcher(),
        reorderWatcher(),
        phoneNumberUpdateWatcher(),
        deliveryNotesUpdateWatcher(),
        updateProfileWatcher(),
        applyReferralCodeWatcher(),
        preOrderCancelWatcher(),
        setPreOrderDateTimeWatcher()
    ])
}
